import { isDevelopment, getApiBaseUrl } from './environment';

/**
 * Centralized API configuration
 */
// Prefer explicit env var -> window-injected -> computed base URL
export const API_BASE_URL = (process.env.REACT_APP_API_URL)
  || (typeof window !== 'undefined' && (window as any)._env_?.REACT_APP_API_URL)
  || getApiBaseUrl();

/**
 * API endpoints - matches Go routes.go
 */
export const API_ENDPOINTS = {
  // Auth (PUBLIC - /api/auth/*)
  auth: {
    register: `${API_BASE_URL}/api/auth/register`,
    login: `${API_BASE_URL}/api/auth/login`,
    loginStudent: `${API_BASE_URL}/api/auth/login/student`,
    health: `${API_BASE_URL}/api/auth/health`,
  },

  // Protected routes (/api/*)
  user: {
    me: `${API_BASE_URL}/api/auth/me`,
  },

  // Public endpoints (no auth required)
  public: {
    skillsAndLinks: `${API_BASE_URL}/api/public/skillsandlinks`,
  },

  // CHISG Graph endpoints (Weaviate-backed)
  chisg: {
    skillsAndLinks: `${API_BASE_URL}/api/chisg/skillsandlinks`,
  },

  // Skills
  skills: {
    heads: `${API_BASE_URL}/api/SkillHeadsGET`,
    get: (id: number) => `${API_BASE_URL}/api/SkillGET/${id}`,
    create: `${API_BASE_URL}/api/SkillPOST`,
    update: (id: number) => `${API_BASE_URL}/api/SkillEDIT/${id}`,
    delete: (id: number) => `${API_BASE_URL}/api/SkillDELETE/${id}`,
    allWithLinks: `${API_BASE_URL}/api/skillsandlinks`,
  },

  // Skill Links
  skillLinks: {
    getByCourse: (id: number) => `${API_BASE_URL}/api/SkillCourseLinksGET/${id}`,
    create: `${API_BASE_URL}/api/SkillLinkPOST`,
    delete: (id: number) => `${API_BASE_URL}/api/SkillLinkDELETE/${id}`,
  },

  // Skills Map
  skillsMap: {
    get: (id: number) => `${API_BASE_URL}/api/SkillsMapGET/${id}`,
    create: `${API_BASE_URL}/api/SkillsMapPOST`,
    delete: (id: number) => `${API_BASE_URL}/api/SkillsMapDELETE/${id}`,
  },

  // Skill Attached (assignments)
  skillAttached: {
    get: (id: number) => `${API_BASE_URL}/api/SkillAttachedGET/${id}`,
    create: `${API_BASE_URL}/api/SkillAttachedPOST`,
    update: `${API_BASE_URL}/api/SkillAttachedPUT`,
    delete: `${API_BASE_URL}/api/SkillAttachedDELETE`,
    byCourse: (id: number) => `${API_BASE_URL}/api/SkillsAttachedCourseGET/${id}`,
    studentCourse: (id: number) => `${API_BASE_URL}/api/StudentCourseSkills/${id}`,
  },

  // Subjects
  subjects: {
    list: `${API_BASE_URL}/api/subjectsALL`,
    get: (id: number) => `${API_BASE_URL}/api/subject/${id}`,
    create: `${API_BASE_URL}/api/subjectPOST`,
    update: (id: number) => `${API_BASE_URL}/api/subjectPUT/${id}`,
    delete: (id: number) => `${API_BASE_URL}/api/subjectDELETE/${id}`,
  },

  // Courses
  courses: {
    list: `${API_BASE_URL}/api/coursesALL`,
    get: (id: number) => `${API_BASE_URL}/api/courseGET/${id}`,
    create: `${API_BASE_URL}/api/coursePOST`,
    update: (id: number) => `${API_BASE_URL}/api/coursePUT/${id}`,
    delete: (id: number) => `${API_BASE_URL}/api/courseDELETE/${id}`,
  },

  // Admin routes (/api/admin/*)
  admin: {
    listStudents: `${API_BASE_URL}/api/admin/students`,
    createStudent: `${API_BASE_URL}/api/admin/students`,
    deleteStudent: (id: number) => `${API_BASE_URL}/api/admin/students/${id}`,
  },

  // Debug
  debug: {
    dbDump: `${API_BASE_URL}/api/debug/dbdump`,
  },

  // Health
  health: `${API_BASE_URL}/health`,
};

// ...existing code...


/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * API response types for better TypeScript support
 */
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T = any> extends ApiResponse<T> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Configuration for API calls
 */
export interface ApiCallOptions extends RequestInit {
  token?: string;
  timeout?: number;
}
/**
 * Default configuration
 */
const DEFAULT_CONFIG: ApiCallOptions = {
  timeout: 10000, // 10 seconds
};

/**
 * Helper function to make authenticated API calls
 */
export const apiCall = async <T = any>(
  endpoint: string,
  options: ApiCallOptions = {}
): Promise<T> => {
  const { token, timeout, ...restOptions } = options;

  const controller = new AbortController();
  const { signal } = controller;

  const headers = new Headers(restOptions.headers || {});
  headers.append('Content-Type', 'application/json');
  headers.append('Accept', 'application/json');

  // This is the critical fix: Add the Authorization header if a token is provided.
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  const config: RequestInit = {
    ...restOptions,
    headers,
    signal,
  };

  const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

  try {
    const response = await fetch(endpoint, config);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

  if (!response.ok) {
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.json();
        errorMessage = errorBody.message || errorBody.error || errorMessage;
      } catch (e) {
        // Ignore if error response is not JSON
      }
      throw new ApiError(errorMessage, response.status, response.statusText);
    }

    // Handle cases where the response body might be empty (e.g., 204 No Content)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json() as T;
    }
    return {} as T; // Return an empty object for non-JSON or empty responses

  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError('Request timed out', 408, 'Request Timeout');
    }
    throw new ApiError(error instanceof Error ? error.message : 'An unknown network error occurred', 0, 'Network Error');
  }
};

const getToken = () => (typeof window !== 'undefined' ? localStorage.getItem('authToken') || undefined : undefined);

/**
 * Specialized API call functions for common operations
 */
export const api = {
  // GET request
  get: <T = any>(endpoint: string, options?: ApiCallOptions) => 
    apiCall<T>(endpoint, { ...options, method: 'GET', token: getToken() }),

  // POST request
  post: <T = any>(endpoint: string, data?: any, options?: ApiCallOptions) =>
    apiCall<T>(endpoint, { 
      ...options, 
      method: 'POST', 
      body: data ? JSON.stringify(data) : undefined,
      token: getToken() 
    }),

  // PUT request
  put: <T = any>(endpoint: string, data?: any, options?: ApiCallOptions) =>
    apiCall<T>(endpoint, { 
      ...options, 
      method: 'PUT', 
      body: data ? JSON.stringify(data) : undefined,
      token: getToken()
    }),

  // PATCH request
  patch: <T = any>(endpoint: string, data?: any, options?: ApiCallOptions) =>
    apiCall<T>(endpoint, { 
      ...options, 
      method: 'PATCH', 
      body: data ? JSON.stringify(data) : undefined,
      token: getToken()
    }),

  // DELETE request
  delete: <T = any>(endpoint: string, options?: ApiCallOptions) =>
    apiCall<T>(endpoint, { ...options, method: 'DELETE', token: getToken() }),
};

/**
 * Hook for using API in React components
 */
export const useApi = () => {

  return api;
  // const getToken = () => localStorage.getItem('authToken') || undefined;

  // return {
  //   apiCall: <T = any>(endpoint: string, options?: ApiCallOptions) =>
  //     apiCall<T>(endpoint, { ...options, token: getToken() }),

  //   get: <T = any>(endpoint: string, options?: ApiCallOptions) =>
  //     api.get<T>(endpoint, { ...options, token: getToken() }),

  //   post: <T = any>(endpoint: string, data?: any, options?: ApiCallOptions) =>
  //     api.post<T>(endpoint, data, { ...options, token: getToken() }),

  //   put: <T = any>(endpoint: string, data?: any, options?: ApiCallOptions) =>
  //     api.put<T>(endpoint, data, { ...options, token: getToken() }),

  //   patch: <T = any>(endpoint: string, data?: any, options?: ApiCallOptions) =>
  //     api.patch<T>(endpoint, data, { ...options, token: getToken() }),

  //   delete: <T = any>(endpoint: string, options?: ApiCallOptions) =>
  //     api.delete<T>(endpoint, { ...options, token: getToken() }),
  // };
};

/**
 * Utility to log API calls in development
 */
export const debugApiCall = (endpoint: string, options: ApiCallOptions) => {
  if (isDevelopment()) {
    console.log(`API Call: ${endpoint}`, {
      baseUrl: API_BASE_URL,
      fullUrl: `${API_BASE_URL}${endpoint}`,
      options,
    });
  }
};

/**
 * Utility to check if we're in development mode
 */
/**
 * Utility to check if we're in development mode
 */
export { isDevelopment };